import React, { useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  ProgressBar,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  Upload,
  FileEarmarkText,
  FileEarmarkPdf,
  FileEarmarkImage,
  FileEarmarkWord,
  Trash,
  CheckCircle,
  XCircle,
  InfoCircle,
  CloudArrowUp,
  ArrowLeft,
  FilePlus,
  Clock,
  ShieldCheck,
} from "react-bootstrap-icons";
import api from "../../services/api";

const DocumentUploadPage = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [documentType, setDocumentType] = useState("passport");

  // Updated color palette based on your theme
  const colors = {
    background: "#032530", // Dark teal
    text: "#FFFFFF", // White text
    accent: "#ff8214", // Orange accent
    accentHover: "#ff9b44", // Lighter orange for hover
    border: "#1e273d", // Dark blue border
    cardBg: "#0b2c36", // Medium teal for card background
    progressBg: "#0b2c36", // Medium teal for progress sections
    success: "#018744", // Green for success
    warning: "#ffaa00", // Yellow for warning
    danger: "#ff4757", // Red for danger
    info: "#018744", // Green for info
  };

  const documentTypes = [
    { value: "passport", label: "Passport Copy", icon: <FileEarmarkImage /> },
    {
      value: "bank_statement",
      label: "Bank Statements",
      icon: <FileEarmarkText />,
    },
    {
      value: "academic_records",
      label: "Academic Records",
      icon: <FileEarmarkText />,
    },
    { value: "sop", label: "Statement of Purpose", icon: <FileEarmarkWord /> },
    { value: "cv", label: "Curriculum Vitae", icon: <FileEarmarkWord /> },
    { value: "fsf", label: "Financial Support Form", icon: <FileEarmarkPdf /> },
    { value: "other", label: "Other Documents", icon: <FileEarmarkText /> },
  ];

  const supportedFormats = [
    { type: "PDF", icon: <FileEarmarkPdf />, color: "#ff8214" }, // Orange
    { type: "DOC", icon: <FileEarmarkWord />, color: "#018744" }, // Green
    { type: "JPG", icon: <FileEarmarkImage />, color: "#ffaa00" }, // Yellow
    { type: "PNG", icon: <FileEarmarkImage />, color: "#1e273d" }, // Dark blue
  ];

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Check file size (10MB max per file)
    const oversizedFiles = selectedFiles.filter(
      (file) => file.size > 10 * 1024 * 1024
    );
    if (oversizedFiles.length > 0) {
      setError(
        `Some files exceed 10MB limit: ${oversizedFiles
          .map((f) => f.name)
          .join(", ")}`
      );
      return;
    }

    const newFiles = selectedFiles.map((file) => ({
      file,
      id: Date.now() + Math.random(),
      uploaded: false,
      error: null,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
    setError(null);
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((file) => file.id !== id));
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      const event = {
        target: {
          files: e.dataTransfer.files,
        },
      };
      handleFileChange(event);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      setError("Please select at least one file");
      return;
    }

    const formData = new FormData();
    formData.append("type", documentType);

    // Append each file
    files.forEach((item) => {
      formData.append("documents", item.file);
    });

    console.log("🚀 Starting upload for appointmentId:", appointmentId);
    console.log("📄 Files to upload:", files.length);
    console.log("📄 Document type:", documentType);

    setUploading(true);
    setError(null);
    setSuccess(false);

    try {
      console.log(
        "📡 Making API request to:",
        `/documents/upload/${appointmentId}`
      );
      const response = await api.post(
        `/documents/upload/${appointmentId}`,
        formData,
        {
          timeout: 60000,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setProgress(percentCompleted);
            }
          },
        }
      );

      console.log("✅ Upload response:", response.data);

      if (response.data.success) {
        setSuccess(true);
        setFiles([]);
        setProgress(0);

        setTimeout(() => {
          navigate("/");
        }, 3000);
      } else {
        setError(response.data.message || "Upload failed");
      }
    } catch (err) {
      console.error("❌ Upload error:", err);

      if (err.response) {
        console.error(
          "❌ Response error:",
          err.response.status,
          err.response.data
        );
        setError(
          err.response.data?.message ||
            err.response.data?.error ||
            `Server error: ${err.response.status}`
        );
      } else if (err.request) {
        console.error("❌ Request error - no response from server");
        setError("No response from server. Please check your connection.");
      } else {
        console.error("❌ Other error:", err.message);
        setError(`Error: ${err.message}`);
      }

      setProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (ext === "pdf") return <FileEarmarkPdf style={{ color: "#ff8214" }} />;
    if (ext === "doc" || ext === "docx")
      return <FileEarmarkWord style={{ color: "#018744" }} />;
    if (ext === "jpg" || ext === "jpeg" || ext === "png")
      return <FileEarmarkImage style={{ color: "#ffaa00" }} />;
    return <FileEarmarkText style={{ color: colors.accent }} />;
  };

  return (
    <div
      style={{
        backgroundColor: colors.background,
        minHeight: "100vh",
        paddingTop: "2rem",
        paddingBottom: "2rem",
      }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            {/* Header */}
            <div className="text-center mb-5">
              <h1
                style={{
                  color: colors.text,
                  fontWeight: "700",
                  marginBottom: "1rem",
                  fontSize: "2.5rem",
                }}
              >
                Document Upload
              </h1>
              <p
                style={{
                  color: colors.text,
                  opacity: 0.8,
                  fontSize: "1.1rem",
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Upload your documents for detailed visa assessment
              </p>
              <div
                style={{
                  color: colors.accent,
                  fontWeight: "600",
                  fontSize: "1rem",
                  marginTop: "1rem",
                }}
              >
                Appointment ID: {appointmentId}
              </div>
            </div>

            <Card
              className="border-0"
              style={{
                backgroundColor: "#13181f",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: `0 10px 40px rgba(0, 0, 0, 0.3)`,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Card.Body style={{ padding: "2.5rem" }}>
                {error && (
                  <Alert
                    variant="danger"
                    onClose={() => setError(null)}
                    dismissible
                    style={{
                      backgroundColor: `${colors.danger}20`,
                      borderColor: colors.danger,
                      color: colors.text,
                      borderRadius: "10px",
                      marginBottom: "2rem",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <XCircle />
                      <strong>Error:</strong> {error}
                    </div>
                  </Alert>
                )}

                {success ? (
                  <div className="text-center py-4">
                    <div
                      style={{
                        color: colors.success,
                        fontSize: "4rem",
                        marginBottom: "1.5rem",
                      }}
                    >
                      <CheckCircle />
                    </div>
                    <h3
                      style={{
                        color: colors.text,
                        fontWeight: "700",
                        marginBottom: "1rem",
                      }}
                    >
                      Documents Uploaded Successfully!
                    </h3>
                    <p
                      style={{
                        color: colors.text,
                        opacity: 0.8,
                        marginBottom: "2rem",
                        fontSize: "1.1rem",
                      }}
                    >
                      Your documents have been submitted for review by our
                      experts.
                    </p>
                    <div
                      style={{
                        backgroundColor: `${colors.success}15`,
                        border: `2px solid ${colors.success}`,
                        borderRadius: "12px",
                        padding: "1.5rem",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <h5 style={{ color: colors.text, marginBottom: "1rem" }}>
                        What happens next?
                      </h5>
                      <div className="text-start">
                        <div className="d-flex align-items-center mb-2">
                          <CheckCircle
                            size={16}
                            className="me-2"
                            style={{ color: colors.success }}
                          />
                          <span style={{ color: colors.text, opacity: 0.8 }}>
                            Documents received and secured
                          </span>
                        </div>
                        <div className="d-flex align-items-center mb-2">
                          <Clock
                            size={16}
                            className="me-2"
                            style={{ color: colors.accent }}
                          />
                          <span style={{ color: colors.text, opacity: 0.8 }}>
                            Expert review within 24-48 hours
                          </span>
                        </div>
                        <div className="d-flex align-items-center">
                          <ShieldCheck
                            size={16}
                            className="me-2"
                            style={{ color: colors.info }}
                          />
                          <span style={{ color: colors.text, opacity: 0.8 }}>
                            Your documents have been uploaded successfully
                          </span>
                        </div>
                      </div>
                    </div>
                    <p
                      style={{
                        color: colors.text,
                        opacity: 0.6,
                        fontSize: "0.9rem",
                        marginTop: "2rem",
                      }}
                    >
                      Redirecting to home page...
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Document Type Selection */}
                    <div className="mb-5">
                      <h5
                        style={{
                          color: colors.text,
                          marginBottom: "1.5rem",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <FileEarmarkText style={{ color: colors.accent }} />
                        Document Type
                      </h5>
                      <div
                        style={{
                          backgroundColor: colors.background,
                          borderRadius: "12px",
                          padding: "1rem",
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        <Form.Select
                          value={documentType}
                          onChange={(e) => setDocumentType(e.target.value)}
                          required
                          style={{
                            backgroundColor: colors.cardBg,
                            border: `1px solid ${colors.border}`,
                            color: colors.text,
                            padding: "12px 14px",
                            borderRadius: "10px",
                            fontSize: "1rem",
                          }}
                        >
                          {documentTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </div>

                    {/* Drag & Drop Area */}
                    <div className="mb-5">
                      <h5
                        style={{
                          color: colors.text,
                          marginBottom: "1.5rem",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <CloudArrowUp style={{ color: colors.accent }} />
                        Upload Documents
                      </h5>

                      <div
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={triggerFileInput}
                        style={{
                          backgroundColor: colors.background,
                          border: `2px dashed ${colors.accent}60`,
                          borderRadius: "16px",
                          padding: "3rem 2rem",
                          textAlign: "center",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${colors.accent}10`;
                          e.currentTarget.style.borderColor = colors.accent;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            colors.background;
                          e.currentTarget.style.borderColor = `${colors.accent}60`;
                        }}
                      >
                        <div
                          style={{
                            color: colors.accent,
                            fontSize: "3rem",
                            marginBottom: "1rem",
                          }}
                        >
                          <Upload />
                        </div>
                        <h4
                          style={{ color: colors.text, marginBottom: "0.5rem" }}
                        >
                          Drag & Drop or Click to Upload
                        </h4>
                        <p
                          style={{
                            color: colors.text,
                            opacity: 0.7,
                            marginBottom: "1.5rem",
                          }}
                        >
                          You can upload multiple documents at once
                        </p>

                        <div className="d-flex justify-content-center gap-3 mb-3">
                          {supportedFormats.map((format) => (
                            <div
                              key={format.type}
                              style={{
                                backgroundColor: colors.cardBg,
                                border: `1px solid ${colors.border}`,
                                borderRadius: "8px",
                                padding: "8px 12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <div style={{ color: format.color }}>
                                {format.icon}
                              </div>
                              <span
                                style={{
                                  color: colors.text,
                                  fontSize: "0.85rem",
                                }}
                              >
                                {format.type}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div
                          style={{
                            color: colors.text,
                            opacity: 0.6,
                            fontSize: "0.85rem",
                          }}
                        >
                          Maximum file size: 10MB per file
                        </div>

                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          style={{ display: "none" }}
                        />
                      </div>

                      {/* Add More Files Button */}
                      {files.length > 0 && (
                        <div className="text-center mt-3">
                          <Button
                            variant="outline"
                            onClick={triggerFileInput}
                            style={{
                              border: `1px solid ${colors.border}`,
                              color: colors.accent,
                              backgroundColor: "transparent",
                              borderRadius: "8px",
                              padding: "8px 20px",
                              fontSize: "0.9rem",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              margin: "0 auto",
                            }}
                          >
                            <FilePlus size={16} />
                            Add More Files
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Selected Files Preview */}
                    {files.length > 0 && (
                      <div className="mb-5">
                        <h5
                          style={{
                            color: colors.text,
                            marginBottom: "1.5rem",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <FileEarmarkText style={{ color: colors.accent }} />
                          Selected Files ({files.length})
                        </h5>

                        <div
                          style={{
                            backgroundColor: colors.background,
                            borderRadius: "12px",
                            padding: "1.5rem",
                            border: `1px solid ${colors.border}`,
                          }}
                        >
                          {files.map((item) => (
                            <div
                              key={item.id}
                              style={{
                                backgroundColor: colors.cardBg,
                                border: `1px solid ${colors.border}`,
                                borderRadius: "10px",
                                padding: "1rem",
                                marginBottom: "0.75rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                transition: "all 0.3s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor =
                                  colors.accent;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor =
                                  colors.border;
                              }}
                            >
                              <div className="d-flex align-items-center gap-3">
                                <div style={{ fontSize: "1.5rem" }}>
                                  {getFileIcon(item.file.name)}
                                </div>
                                <div>
                                  <div
                                    style={{
                                      color: colors.text,
                                      fontWeight: "500",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    {item.file.name}
                                  </div>
                                  <div
                                    style={{
                                      color: colors.text,
                                      opacity: 0.7,
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {formatFileSize(item.file.size)}
                                  </div>
                                </div>
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeFile(item.id)}
                                style={{
                                  border: `1px solid ${colors.danger}40`,
                                  color: colors.danger,
                                  backgroundColor: "transparent",
                                  borderRadius: "6px",
                                  padding: "4px 10px",
                                  fontSize: "0.8rem",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Trash size={12} />
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Upload Progress */}
                    {uploading && (
                      <div className="mb-5">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5
                            style={{
                              color: colors.text,
                              fontWeight: "600",
                              marginBottom: 0,
                            }}
                          >
                            Uploading Documents...
                          </h5>
                          <div
                            style={{
                              color: colors.accent,
                              fontWeight: "700",
                              fontSize: "1.1rem",
                            }}
                          >
                            {progress}%
                          </div>
                        </div>
                        <ProgressBar
                          now={progress}
                          style={{
                            height: "10px",
                            backgroundColor: colors.border,
                            borderRadius: "5px",
                            overflow: "hidden",
                          }}
                        >
                          <ProgressBar
                            animated
                            style={{
                              backgroundColor: colors.accent,
                              background: `linear-gradient(90deg, ${colors.accent}, ${colors.accentHover})`,
                            }}
                          />
                        </ProgressBar>
                        <div
                          style={{
                            color: colors.text,
                            opacity: 0.7,
                            fontSize: "0.85rem",
                            textAlign: "right",
                            marginTop: "8px",
                          }}
                        >
                          {files.length} file{files.length !== 1 ? "s" : ""}{" "}
                          uploading
                        </div>
                      </div>
                    )}

                    {/* Information Panel */}
                    <div
                      style={{
                        backgroundColor: `${colors.info}15`,
                        border: `1px solid ${colors.info}`,
                        borderRadius: "12px",
                        padding: "1.5rem",
                        marginBottom: "2rem",
                      }}
                    >
                      <div className="d-flex align-items-start gap-3">
                        <InfoCircle
                          size={20}
                          style={{
                            color: colors.info,
                            marginTop: "2px",
                          }}
                        />
                        <div>
                          <h6
                            style={{
                              color: colors.text,
                              marginBottom: "0.5rem",
                            }}
                          >
                            Important Information
                          </h6>
                          <ul
                            style={{
                              color: colors.text,
                              opacity: 0.8,
                              fontSize: "0.9rem",
                              marginBottom: 0,
                              paddingLeft: "1rem",
                            }}
                          >
                            <li>You can upload multiple documents in one go</li>
                            <li>Supported formats: PDF, DOC, DOCX, JPG, PNG</li>
                            <li>Maximum file size: 10MB per document</li>
                            <li>Documents are encrypted and securely stored</li>
                            <li>
                              Our experts will review your documents within
                              24-48 hours
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="outline"
                        onClick={() => navigate("/")}
                        disabled={uploading}
                        style={{
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          borderRadius: "10px",
                          padding: "12px 24px",
                          backgroundColor: "transparent",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <ArrowLeft size={16} />
                        Cancel
                      </Button>

                      <Button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={uploading || files.length === 0}
                        style={{
                          backgroundColor: colors.accent,
                          borderColor: colors.accent,
                          color: colors.background,
                          borderRadius: "10px",
                          padding: "12px 32px",
                          fontSize: "1rem",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          opacity: files.length === 0 ? 0.6 : 1,
                          transition: "all 0.3s ease",
                          boxShadow:
                            files.length === 0
                              ? "none"
                              : `0 5px 20px rgba(255, 130, 20, 0.3)`,
                        }}
                        onMouseEnter={(e) => {
                          if (files.length > 0 && !uploading) {
                            e.currentTarget.style.backgroundColor =
                              colors.accentHover;
                            e.currentTarget.style.borderColor =
                              colors.accentHover;
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.boxShadow = `0 8px 25px rgba(255, 155, 68, 0.4)`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (files.length > 0 && !uploading) {
                            e.currentTarget.style.backgroundColor =
                              colors.accent;
                            e.currentTarget.style.borderColor = colors.accent;
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = `0 5px 20px rgba(255, 130, 20, 0.3)`;
                          }
                        }}
                      >
                        {uploading ? (
                          <>Uploading...</>
                        ) : (
                          <>
                            <Upload size={18} />
                            Upload {files.length} Document
                            {files.length !== 1 ? "s" : ""}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Custom Styles */}
      <style jsx="true">{`
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: ${colors.background};
        }

        ::-webkit-scrollbar-thumb {
          background: ${colors.accent};
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${colors.accentHover};
        }

        .form-control:focus,
        .form-select:focus {
          background-color: ${colors.cardBg};
          border-color: ${colors.accent};
          color: ${colors.text};
          box-shadow: 0 0 0 0.25rem rgba(255, 130, 20, 0.25);
        }

        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};

export default DocumentUploadPage;
